import { fmtDate } from '../../utils/datetime';
import React, { useState, useEffect } from "react";
import { IHeadQuarter, ILocation } from "./utils/interface";
import { toast } from '../../utils/toast';
import * as Yup from "yup";
import { useFormik } from "formik";
import { useLoadScript } from "@react-google-maps/api";
import { Plus, Edit3, Trash2, User, Search, X, Building2 } from "lucide-react";
import PlacesAutocompleteInput from "./utils/PlacesAutocompleteInput";
import config from "../../config";
import { Modal } from "./utils/Modal";
import { createNewLocation, deleteLocation, editLocation, getAllHeadquarters, getAllLocations } from "../../api/master";
import { useAuth } from "../../context/AuthContext";

type EditData = {
  head_quarter_id: any,
  location_id: any,
  latitude: any,
  longitude: any
}

const Location: React.FC = () => {
  const libraries: any = ["places"];
  const googleMapsApiKey: string = config.googleMapsApiKey;
  console.log("testing////////////////")

  const token = localStorage.getItem("authToken");
  const { user } = useAuth();
  const [location, setLocation] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const [editData, setEditData] = useState<EditData>({ location_id: "", head_quarter_id: "", latitude: "", longitude: "" });

  const [headQuarter, setHeadQuarter] = useState([]);
  const [search, setSearch] = useState("");
  const [hqFilter, setHqFilter] = useState("");

  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [placeSearchValue, setPlaceSearchValue] = useState("");
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: googleMapsApiKey,
    libraries: libraries,
  });
  useEffect(() => {
    if (user && user.tenant_id) {
      getLocations();
      getHeadQuarters();
    }
  }, [user]);

  /**Add New Zone */
  const initialValues = {
    name: "",
    head_quarter_id: "",
    is_hq: false,
  };

  const ZoneSchema = Yup.object({
    name: Yup.string().required("Please enter Location"),
  });
  const {
    setValues,
    values,
    handleBlur,
    handleChange,
    handleSubmit,
    errors,
    touched,
  } = useFormik({
    initialValues,
    validationSchema: ZoneSchema,
    onSubmit: async (values) => {
      if (!isEdit) {
        try {
          const response = await createNewLocation(values.name, user.tenant_id, lat, lng, values.head_quarter_id, values.is_hq)

          if (response.status === 200) {
            getLocations();
            setIsAddModalOpen(false);
            setValues({ name: "", head_quarter_id: "", is_hq: false });
            toast.success("Location Created Successfully!");
          }
        } catch (error: any) {
          toast.error(error.message || "Something went wrong.");
        }
      }

      if (isEdit) {
        try {
          const response = await editLocation(values.name, user.tenant_id, lat, lng, values.head_quarter_id, editData.location_id, values.is_hq);

          if (response.status === 200) {
            getLocations();
            setValues({ name: "", head_quarter_id: "", is_hq: false });
            setEditData({ ...editData, head_quarter_id: 0 });
            setIsEdit(false);
            setIsEditModalOpen(false);
            toast.success("Location Updated Successfully!");
          }
        } catch (error: any) {
          toast.error(error.message || "Something went wrong.");
        }
      }
    },
  });

  const getHeadQuarters = async () => {
    try {
      const response = await getAllHeadquarters(user.tenant_id);
      setHeadQuarter(response);
    } catch (error) { }
  };

  const getLocations = async () => {
    try {
      const response = await getAllLocations(user.tenant_id);
      setLocation(response);
    } catch (error) { }
  };

  const handleEdit = (item: ILocation) => {
    setValues({ name: item.name, head_quarter_id: item.head_quarter_id, is_hq: !!item.is_hq });
    setEditData({
      location_id: item?.id,
      head_quarter_id: item?.head_quarter_id,
      latitude: item?.latitude,
      longitude: item?.longitude,
    });
    setLat(String(item?.latitude ?? ""));
    setLng(String(item?.longitude ?? ""));
    setPlaceSearchValue(item?.name ?? "");
    setIsEditModalOpen(true);
    setIsEdit(true);
  };

  const handleDelete = async (id: number) => {
    if (!await toast.confirm("This action cannot be undone.", "Delete location?", "Delete")) return;
    try {
      await deleteLocation(id);
      setLocation(location.filter((location: ILocation) => location.id !== id));
      toast.success("Location deleted successfully.");
    } catch (error: any) {
      toast.error(error.message || "Failed to delete location.");
    }
  };

  const filteredLocations = (location as ILocation[]).filter((item) => {
    const matchSearch = !search || item.name.toLowerCase().includes(search.toLowerCase());
    const matchHq = !hqFilter || String(item.head_quarter_id) === hqFilter;
    return matchSearch && matchHq;
  });

  return (
    <div className="p-4 md:p-8">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="flex flex-col items-start justify-between gap-4 mb-8 md:flex-row md:items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Location Master</h1>
            <p className="mt-1 text-blue-600">Location</p>
          </div>
          <button
            onClick={() => { setLat(""); setLng(""); setPlaceSearchValue(""); setValues({ name: "", head_quarter_id: "", is_hq: false }); setIsAddModalOpen(true); }}
            className="flex items-center px-4 py-2 text-white transition-colors bg-blue-600 rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Location
          </button>
        </div>

        {/* Master Data List */}
        <div className="bg-white shadow-md rounded-xl">
          <div className="p-6">
            {/* Header + Search + Filter */}
            <div className="flex flex-col justify-between gap-3 mb-6 md:flex-row md:items-center">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 shrink-0">
                <User className="w-5 h-5 text-blue-600" />
                Location
                <span className="text-sm font-normal text-gray-500">
                  ({filteredLocations.length}{filteredLocations.length !== location.length ? ` / ${location.length}` : ''})
                </span>
              </h3>

              <div className="flex flex-col w-full gap-2 sm:flex-row md:w-auto">
                {/* Search */}
                <div className="relative flex-1 sm:w-56">
                  <Search className="absolute w-4 h-4 text-gray-400 -translate-y-1/2 pointer-events-none left-3 top-1/2" />
                  <input
                    type="text"
                    placeholder="Search locations..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full py-2 pr-8 text-sm border border-gray-300 rounded-lg pl-9 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {search && (
                    <button onClick={() => setSearch("")} className="absolute text-gray-400 -translate-y-1/2 right-2 top-1/2 hover:text-gray-600">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {/* HQ Filter */}
                <select
                  value={hqFilter}
                  onChange={(e) => setHqFilter(e.target.value)}
                  className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent sm:w-48"
                >
                  <option value="">All Headquarters</option>
                  {(headQuarter as IHeadQuarter[]).map((hq) => (
                    <option key={hq.id} value={String(hq.id)}>{hq.name}</option>
                  ))}
                </select>

                {/* Clear filters */}
                {(search || hqFilter) && (
                  <button
                    onClick={() => { setSearch(""); setHqFilter(""); }}
                    className="flex items-center gap-1 px-3 py-2 text-sm text-gray-500 transition-colors border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap"
                  >
                    <X className="w-3.5 h-3.5" />
                    Clear
                  </button>
                )}
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="px-4 py-3 font-medium text-left text-gray-700">
                      SN
                    </th>

                    <th className="px-4 py-3 font-medium text-left text-gray-700">
                      Name
                    </th>

                    <th className="px-4 py-3 font-medium text-left text-gray-700">
                      Head Quarter
                    </th>

                    <th className="px-4 py-3 font-medium text-left text-gray-700">
                      HQ
                    </th>

                    <th className="px-4 py-3 font-medium text-left text-gray-700">
                      Latitude
                    </th>
                    <th className="px-4 py-3 font-medium text-left text-gray-700">
                      Longitude
                    </th>

                    <th className="px-4 py-3 font-medium text-left text-gray-700">
                      Updated At
                    </th>

                    <th className="px-4 py-3 font-medium text-left text-gray-700">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLocations.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="py-12 text-sm text-center text-gray-400">
                        {search || hqFilter ? "No locations match the current filters." : "No locations found."}
                      </td>
                    </tr>
                  ) : filteredLocations.map((item: ILocation, index) => (
                    <tr
                      key={item.id}
                      className="transition-colors border-b border-gray-100 hover:bg-blue-50"
                    >
                      <td className="px-4 py-4">
                        <div className="flex items-center">
                          <div className="font-medium text-gray-900">
                            {index + 1}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center">
                          <div className="font-medium text-gray-900">
                            {item?.name}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center">
                          <div className="font-medium text-gray-900">
                            {item?.headquarter?.name}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        {item?.is_hq ? (
                          <span className="px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded-full">Yes</span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-medium text-gray-500 bg-gray-100 rounded-full">No</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center">
                          <div className="font-medium text-gray-900">
                            {item?.latitude}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center">
                          <div className="font-medium text-gray-900">
                            {item?.longitude}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center">
                          <div className="font-medium text-gray-900">
                            {fmtDate(item.updated_at)}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(item)}
                            className="p-2 transition-colors border border-gray-300 rounded hover:bg-gray-100"
                            title="Edit"
                            type="button"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>

                          <button
                            onClick={() => handleDelete(item.id)}
                            className="p-2 text-white transition-colors bg-red-600 rounded hover:bg-red-700"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Add Modal */}
        <Modal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          title={`Add New Location`}
        >
          <div className="space-y-4">
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Select Head Quarter *
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              name="head_quarter_id"
              value={values.head_quarter_id}
              onBlur={handleBlur}
              onChange={handleChange}
            >
              <option value="">--Select Head Quarter--</option>
              {headQuarter &&
                headQuarter.map((c: IHeadQuarter) => {
                  return (
                    <>
                      <option value={c.id}>{c.name}</option>
                    </>
                  );
                })}
            </select>
            {errors.head_quarter_id && touched.head_quarter_id && (
              <p className="text-red-500">{errors.head_quarter_id}</p>
            )}
            <form onSubmit={handleSubmit}>
              <div>
                {isLoaded ? (
                  <PlacesAutocompleteInput
                    onPlaceSelect={(name, lat, lng) => {
                      setLat(lat);
                      setLng(lng);
                      setPlaceSearchValue(name);
                      setValues({ name, head_quarter_id: values.head_quarter_id, is_hq: values.is_hq });
                    }}
                  />
                ) : (
                  <p className="mb-3 text-sm text-gray-500">Loading maps...</p>
                )}

                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Name *
                </label>
                <input
                  name="name"
                  value={values.name}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {errors.name && touched.name && (
                  <p className="text-red-500">{errors.name}</p>
                )}

                <div className={`flex items-center justify-between gap-4 p-3 mt-4 border rounded-lg transition-colors ${values.is_hq ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-gray-50'}`}>
                  <div className="flex items-center gap-3">
                    <div className={`flex items-center justify-center w-9 h-9 rounded-lg shrink-0 ${values.is_hq ? 'bg-blue-600' : 'bg-gray-300'}`}>
                      <Building2 className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Headquarters Location</p>
                      <p className="text-xs text-gray-500">Mark this location as a HQ</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setValues({ ...values, is_hq: !values.is_hq })}
                    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${values.is_hq ? 'bg-blue-600' : 'bg-gray-300'}`}
                    role="switch"
                    aria-checked={values.is_hq}
                  >
                    <span
                      className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${values.is_hq ? 'translate-x-6' : 'translate-x-1'}`}
                    />
                  </button>
                </div>
              </div>

              <div className="flex justify-end pt-4 space-x-3">
                <button type="button" className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add Location
                </button>
              </div>
            </form>
          </div>
        </Modal>

        {/* Edit Modal */}
        <Modal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          title="Edit Location"
        >
          <div className="space-y-4">
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Select HeadQuarter *
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              name="head_quarter_id"
              value={values.head_quarter_id}
              onBlur={handleBlur}
              onChange={handleChange}
            >
              <option value="">--Select HeadQuarter--</option>
              {headQuarter &&
                headQuarter.map((c: IHeadQuarter) => {
                  return (
                    <>
                      <option value={c.id}>{c.name}</option>
                    </>
                  );
                })}
            </select>
            {errors.head_quarter_id && touched.head_quarter_id && (
              <p className="text-red-500">{errors.head_quarter_id}</p>
            )}
            <form onSubmit={handleSubmit}>
              <div>
                {placeSearchValue && (
                  <p className="mb-2 text-sm text-gray-500">
                    Current: <span className="font-medium text-gray-700">{placeSearchValue}</span>
                  </p>
                )}
                {isLoaded ? (
                  <PlacesAutocompleteInput
                    onPlaceSelect={(name, lat, lng) => {
                      setLat(lat);
                      setLng(lng);
                      setPlaceSearchValue(name);
                      setValues({ name, head_quarter_id: values.head_quarter_id, is_hq: values.is_hq });
                    }}
                  />
                ) : (
                  <p className="mb-3 text-sm text-gray-500">Loading maps...</p>
                )}

                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Name *
                </label>
                <input
                  name="name"
                  value={values.name}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {errors.name && touched.name && (
                  <p className="text-red-500">{errors.name}</p>
                )}

                <div className={`flex items-center justify-between gap-4 p-3 mt-4 border rounded-lg transition-colors ${values.is_hq ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-gray-50'}`}>
                  <div className="flex items-center gap-3">
                    <div className={`flex items-center justify-center w-9 h-9 rounded-lg shrink-0 ${values.is_hq ? 'bg-blue-600' : 'bg-gray-300'}`}>
                      <Building2 className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Headquarters Location</p>
                      <p className="text-xs text-gray-500">Mark this location as a HQ</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setValues({ ...values, is_hq: !values.is_hq })}
                    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${values.is_hq ? 'bg-blue-600' : 'bg-gray-300'}`}
                    role="switch"
                    aria-checked={values.is_hq}
                  >
                    <span
                      className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${values.is_hq ? 'translate-x-6' : 'translate-x-1'}`}
                    />
                  </button>
                </div>
              </div>

              <div className="flex justify-end pt-4 space-x-3">
                <button type="button" className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Update
                </button>
              </div>
            </form>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default Location;
